// src/pages/Home.js
import { Box, Button, Container } from "@mui/material"; // Asegúrate de que esta importación termina con punto y coma
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// Providers (importación separada)

import {
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore"; // Añade doc y onSnapshot
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

// Icons
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircle from "@mui/icons-material/CheckCircle";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ForumIcon from "@mui/icons-material/Forum";
import GroupIcon from "@mui/icons-material/Group";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SmartToyIcon from "@mui/icons-material/SmartToy";

// Componentes
import BlogPostCard from "../components/blog/BlogPostCard";
import ChatbotButton from "../components/chatbot/ChatbotButton";
import ChatbotWindow from "../components/chatbot/ChatbotWindow";
import { db } from "../services/firebase";
import "../styles/pages/Home.css";

// Paso 1: Importar el hook useSubscription
import { useSubscription } from "../context/SubscriptionProvider";

// Añadir esta importación junto a las demás importaciones de hooks
import { useSnackbar } from "notistack";

// Importar el nuevo componente
import FeedbackModal from "../components/admin/FeedbackModal"; // Cambiado 'feedback' por 'admin'
import SubscriptionModal from "../components/chatbot/SubscriptionModal";
import FeatureShowcase from "../components/home/FeatureShowcase"; // <-- Añade esta línea
import StoryModal from "../components/home/StoryModal"; // Asegúrate que StoryModal también esté importado

// En Home.js, añadir la importación al inicio

// Lazy loading para componentes pesados
const Testimonials = lazy(() => import("../components/home/Testimonials"));

// Añadir al principio del archivo, después de las importaciones:
const ScreenReaderOnly = ({ children }) => (
  <span
    style={{
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      borderWidth: "0",
    }}
  >
    {children}
  </span>
);

// Añadir esta función helper al principio del archivo:
const safeTranslate = (key, defaultText, t, params = {}) => {
  try {
    const translated = t(key, params);
    // Si la traducción devuelve exactamente la clave, significa que no existe
    return translated === key ? defaultText : translated;
  } catch (error) {
    console.warn(`Translation error for key: ${key}`, error);
    return defaultText;
  }
};

// Añadir esta constante justo antes del componente Home (aproximadamente línea 144)
const featureIcons = {
  blog: <ArticleIcon sx={{ fontSize: 50, color: "#ff7675" }} />,
  specialists: <LocalHospitalIcon sx={{ fontSize: 50, color: "#74b9ff" }} />,
  forum: <ForumIcon sx={{ fontSize: 50, color: "#a29bfe" }} />,
  chatbot: <SmartToyIcon sx={{ fontSize: 50, color: "#55efc4" }} />,
};

// React.memo para componentes pesados
const FeatureCard = React.memo(
  ({ feature, hovered, onHover, onClick, icons }) => {
    // Obtén t y theme directamente dentro del componente
    const { t } = useTranslation();
    const theme = useTheme();

    return (
      <motion.div
        whileHover={{
          scale: 1.03,
          boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2)",
        }}
        whileTap={{ scale: 0.98 }}
        onMouseEnter={() => onHover(feature)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onClick(`/${feature}`)}
        style={{ cursor: "pointer", height: "100%" }}
      >
        <Paper
          elevation={6}
          sx={{
            borderRadius: "20px",
            height: "100%",
            minHeight: { xs: 280, sm: 320 },
            display: "flex",
            flexDirection: "column",
            p: { xs: 3, sm: 3.5 },
            transition: "all 0.3s ease",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
            "&:hover": {
              transform: "translateY(-6px)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 16px 32px rgba(0,0,0,0.4)"
                  : "0 16px 32px rgba(0,0,0,0.12)",
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              height: "100%",
            }}
          >
            <Box sx={{ mb: 1.5 }}>{icons[feature]}</Box>
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: "'Quicksand', sans-serif",
                fontSize: { xs: "1.4rem", sm: "1.6rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              {t(`home.features.${feature}.title`)}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                mb: 3,
                fontSize: "1rem",
                flexGrow: 1,
              }}
            >
              {t(`home.features.${feature}.description`)}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: "auto",
                borderRadius: "8px",
                px: 2,
                py: 1.2, // Mayor altura para mejor accesibilidad
                fontWeight: 600,
                textTransform: "none", // Texto más natural
                minHeight: "44px", // Altura mínima para accesibilidad táctil
              }}
              component={RouterLink}
              to={`/${feature}`}
              aria-label={t(
                `home.features.${feature}.buttonAriaLabel`,
                `Explorar la sección de ${feature}`,
              )}
            >
              {t("home.features.button")}
            </Button>
          </Box>
        </Paper>
      </motion.div>
    );
  },
);

// Mejora de memoización para componentes pesados
// Añadir después de las importaciones existentes:
const MemoizedFeatureCard = React.memo(FeatureCard);
const MemoizedBlogPostCard = React.memo(BlogPostCard);

// Cambiar esta línea:
// const Home = ({ toggleTheme, mode }) => {
// Por esta:
const Home = () => {
  // Paso 2: Usar el mismo contexto de suscripción que en Subscription.js
  const { isSubscribed } = useSubscription();
  const { enqueueSnackbar } = useSnackbar(); // Añadir esta línea

  // Paso 3: Añadir estados para el chat
  const [chatbotWindowOpen, setChatbotWindowOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // Añadir un nuevo estado
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Dentro del componente Home, añadir este estado
  const [storyModalOpen, setStoryModalOpen] = useState(false);

  // Añadir al principio del componente, justo después de las variables de estado:
  const isLowPowerDevice =
    useMediaQuery("(prefers-reduced-motion: reduce)") ||
    navigator.connection?.saveData ||
    navigator.connection?.effectiveType === "slow-2g" ||
    navigator.connection?.effectiveType === "2g";

  // --- ESTADOS PARA EL BANNER Y FEEDBACK ---
  const [bannerConfig, setBannerConfig] = useState(null); // Guarda la config leída de Firestore
  const [loadingBanner, setLoadingBanner] = useState(true); // Estado de carga para el banner
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false); // Controla visibilidad del modal

  // --- HANDLERS PARA EL MODAL DE FEEDBACK ---
  const handleOpenFeedbackModal = useCallback(() => {
    setIsFeedbackModalOpen(true);
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setIsFeedbackModalOpen(false);
  }, []);

  // Paso 4: Implementar la función para abrir el chat
  const handleOpenChatWindow = useCallback(() => {
    console.log("Abriendo ventana de chat");

    if (isSubscribed) {
      // Si está suscrito, abre el chat normalmente
      setChatbotWindowOpen(true);
      setHasUnreadMessages(false);
    } else {
      // Si no está suscrito, muestra el modal
      setSubscriptionModalOpen(true);
    }
  }, [isSubscribed, setChatbotWindowOpen]);

  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const heroRef = useRef(null);

  // Estados
  const [latestPosts, setLatestPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("hero");
  const [hoveredFeature, setHoveredFeature] = useState(null);
  // --> NUEVOS ESTADOS <--
  const [latestTopic, setLatestTopic] = useState(null); // Para guardar el último tema
  const [loadingLatestTopic, setLoadingLatestTopic] = useState(true); // Para el estado de carga

  // Extraer fetchLatestPosts como función memoizada
  const fetchLatestPosts = useCallback(
    async (retryCount = 0) => {
      try {
        setLoading(true);
        setError(null);

        const postsQuery = query(
          collection(db, "blog"), // CORREGIDO: blog es el nombre correcto de la colección
          orderBy("publishedAt", "desc"),
          limit(3),
        );

        const querySnapshot = await getDocs(postsQuery);

        if (querySnapshot.empty) {
          setLatestPosts([]);
        } else {
          const postsData = querySnapshot.docs.map((doc) => {
            const data = doc.data();

            // Generar excerpt si no existe
            if (!data.excerpt) {
              // Primero intentar usar seoMetadata.description si existe
              if (data.seoMetadata?.description) {
                data.excerpt = data.seoMetadata.description;
              }
              // Si no hay seoMetadata.description, usar content
              else if (data.content) {
                const plainText = data.content
                  .replace(/#+\s+/g, "") // Eliminar encabezados markdown
                  .replace(/\*\*/g, "") // Eliminar negritas
                  .replace(/\n/g, " ") // Convertir saltos de línea en espacios
                  .trim();

                data.excerpt = plainText.substring(0, 150) + "...";
              }
            }

            return {
              id: doc.id,
              ...data,
              // Validar campos críticos (mantener este código)
              title: data.title || t("blog.untitledPost"),
              publishedAt: data.publishedAt || new Date(),
              imageUrl: data.imageUrl || "/assets/images/blog/default.jpg",
              excerpt: data.excerpt || "", // AÑADIR ESTA LÍNEA para asegurar que el excerpt generado se pase a BlogPostCard
            };
          });
          setLatestPosts(postsData);
        }
      } catch (error) {
        console.error("Error fetching latest posts:", error);

        // Implementar retry pattern (máximo 3 intentos)
        if (retryCount < 3) {
          console.log(`Reintentando (${retryCount + 1}/3)...`);
          setTimeout(
            () => fetchLatestPosts(retryCount + 1),
            1000 * (retryCount + 1),
          );
          return;
        }

        setError(t("home.articles.fetchError"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  // --> NUEVA FUNCIÓN <--
  const fetchLatestTopic = useCallback(async () => {
    setLoadingLatestTopic(true);
    try {
      // Asegúrate que 'forum_topics' es el nombre correcto de tu colección
      // y 'createdAt' el campo correcto para ordenar por creación más reciente
      const topicsQuery = query(
        collection(db, "forum_topics"),
        orderBy("createdAt", "desc"),
        limit(1),
      );
      const querySnapshot = await getDocs(topicsQuery);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        setLatestTopic({
          id: doc.id,
          title:
            doc.data().title ||
            t("home.pulse.untitledTopic", "Tema sin título"),
          // Podrías añadir más datos si los necesitas (autor, fecha, etc.)
        });
      } else {
        setLatestTopic(null); // No hay temas
      }
    } catch (err) {
      console.error("Error fetching latest forum topic:", err);
      setLatestTopic(null); // Manejar error estableciendo a null
      // Podrías añadir un mensaje de error específico si quieres
    } finally {
      setLoadingLatestTopic(false);
    }
  }, [t]); // t como dependencia por si usas traducciones en errores o títulos por defecto

  // Observador de intersección para detectar secciones visibles
  useEffect(() => {
    // Verificar preferencia de usuario para movimiento reducido
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);

          // Scroll suave solo si el usuario no prefiere movimiento reducido
          if (!prefersReducedMotion && entry.target.id !== activeSection) {
            const announcement = document.createElement("div");
            announcement.setAttribute("role", "status");
            announcement.setAttribute("aria-live", "polite");
            announcement.textContent = t(`home.${entry.target.id}.title`);
            document.body.appendChild(announcement);
            setTimeout(() => announcement.remove(), 1000);
          }
        }
      });
    }, options);

    // Observar todas las secciones
    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [activeSection, t]);

  // Añadir este useEffect para utilizar activeSection
  useEffect(() => {
    if (activeSection) {
      // Puedes añadir una clase activa a la sección actual
      const allSections = document.querySelectorAll("section[id]");
      allSections.forEach((section) => {
        if (section.id === activeSection) {
          section.classList.add("active-section");
        } else {
          section.classList.remove("active-section");
        }
      });

      // También podemos usarlo para actualizar el título de la página
      document.title = `VicDown - ${t(
        `home.${activeSection}.pageTitle`,
        "Comunidad para síndrome de Down",
      )}`;
    }
  }, [activeSection, t]);

  // Añadir justo después del useEffect para activeSection:
  useEffect(() => {
    if (activeSection) {
      // Actualizar metadatos dinámicamente según la sección activa
      const metaTitles = {
        hero: t(
          "home.hero.metaTitle",
          "VicDown - Comunidad para síndrome de Down",
        ),
        about: t("home.about.metaTitle", "Sobre VicDown - Nuestra misión"),
        features: t("home.features.metaTitle", "Servicios de VicDown"),
        testimonials: t(
          "home.testimonials.metaTitle",
          "Experiencias con VicDown",
        ),
        blog: t("home.blog.metaTitle", "Artículos sobre síndrome de Down"),
      };

      const metaDescriptions = {
        hero: t(
          "home.hero.metaDescription",
          "VicDown, una comunidad de apoyo para personas con síndrome de Down y sus familias.",
        ),
        about: t(
          "home.about.metaDescription",
          "Conoce nuestra misión y visión de crear una comunidad inclusiva.",
        ),
        features: t(
          "home.features.metaDescription",
          "Descubre nuestros servicios: chatbot, especialistas, foro y recursos.",
        ),
        testimonials: t(
          "home.testimonials.metaDescription",
          "Experiencias reales de familias que forman parte de nuestra comunidad.",
        ),
        blog: t(
          "home.blog.metaDescription",
          "Artículos informativos sobre síndrome de Down, desarrollo y educación inclusiva.",
        ),
      };

      // Actualizar title y meta description
      document.title = metaTitles[activeSection] || metaTitles.hero;

      // Actualizar meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        document.head.appendChild(metaDescription);
      }
      metaDescription.content =
        metaDescriptions[activeSection] || metaDescriptions.hero;
    }
  }, [activeSection, t, fetchLatestPosts]);

  // MANTENER solo este useEffect para cargar posts (que ya existe)
  useEffect(() => {
    fetchLatestPosts();
    // --> AÑADE ESTA LÍNEA <--
    fetchLatestTopic();
  }, [fetchLatestPosts, fetchLatestTopic]); // <-- AÑADE fetchLatestTopic AQUÍ TAMBIÉN

  // Añadir en Home.js
  useEffect(() => {
    if (isSubscribed) {
      // Verificar una vez al inicio que la conexión a Firebase y a la API de GPT-4 funciona
      const verifyConnections = async () => {
        try {
          // Verificar Firebase (consulta simple)
          const testQuery = query(collection(db, "config"), limit(1));
          await getDocs(testQuery);

          console.log("Conexión a Firebase verificada");
        } catch (error) {
          console.error("Error de conexión a Firebase:", error);
          // Usar enqueueSnackbar solo si está disponible
          if (enqueueSnackbar) {
            enqueueSnackbar(
              "Problemas de conexión. Algunas funciones podrían no estar disponibles.",
              {
                variant: "warning",
              },
            );
          } else {
            console.warn(
              "Problemas de conexión. Algunas funciones podrían no estar disponibles.",
            );
          }
        }
      };

      verifyConnections();
    }
  }, [isSubscribed, enqueueSnackbar]); // Añadir enqueueSnackbar a las dependencias

  // Añadir detección de conectividad:
  useEffect(() => {
    const handleOnline = () => {
      enqueueSnackbar(
        t(
          "connectivity.restored",
          "Conexión restablecida. La aplicación está actualizada.",
        ),
        { variant: "success" },
      );
      // Recargar datos críticos
      fetchLatestPosts();
    };

    const handleOffline = () => {
      enqueueSnackbar(
        t(
          "connectivity.lost",
          "No hay conexión a internet. Algunas funciones podrían no estar disponibles.",
        ),
        { variant: "warning", autoHideDuration: null },
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Si ya está offline al montar, mostrar advertencia
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [enqueueSnackbar, t, fetchLatestPosts]);

  // --- useEffect para leer la configuración del Banner en tiempo real ---
  useEffect(() => {
    setLoadingBanner(true);
    // ¡¡IMPORTANTE!! Ajusta 'site_config' y 'banner' si tu colección/documento se llaman diferente
    const bannerConfigRef = doc(db, "site_config", "banner");

    const unsubscribe = onSnapshot(
      bannerConfigRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setBannerConfig(docSnap.data());
        } else {
          console.log("No se encontró configuración para el banner.");
          setBannerConfig({ enabled: false }); // Deshabilita si no existe config
        }
        setLoadingBanner(false);
      },
      (error) => {
        console.error("Error al leer configuración del banner:", error);
        setBannerConfig({ enabled: false }); // Deshabilita en caso de error
        setLoadingBanner(false);
      },
    );

    return () => unsubscribe(); // Limpiar listener al desmontar
  }, []); // Ejecutar solo una vez al montar

  // Funciones interactivas
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleFeatureHover = useCallback(
    (feature) => {
      setHoveredFeature(feature);
    },
    [setHoveredFeature],
  );

  const handleFeatureClick = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate],
  );

  // Variantes de animación para elementos
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Mejorar navegación por teclado
  useEffect(() => {
    const handleKeyNavigation = (e) => {
      // Permitir navegación por secciones con teclas de flecha cuando se presiona ALT
      if (e.altKey) {
        const sections = ["hero", "about", "features", "testimonials", "blog"];
        const currentIndex = sections.indexOf(activeSection);

        if (e.key === "ArrowDown" && currentIndex < sections.length - 1) {
          scrollToSection(sections[currentIndex + 1]);
          // Anunciar al lector de pantalla
          announceToScreenReader(
            t("accessibility.navigatedTo", "Navegando a") +
              " " +
              t(`home.${sections[currentIndex + 1]}.title`),
          );
        } else if (e.key === "ArrowUp" && currentIndex > 0) {
          scrollToSection(sections[currentIndex - 1]);
          announceToScreenReader(
            t("accessibility.navigatedTo", "Navegando a") +
              " " +
              t(`home.${sections[currentIndex - 1]}.title`),
          );
        }
      }

      // Acceso rápido con Alt+Números
      if (
        e.altKey &&
        !isNaN(parseInt(e.key)) &&
        parseInt(e.key) >= 1 &&
        parseInt(e.key) <= 5
      ) {
        const sections = ["hero", "about", "features", "testimonials", "blog"];
        const sectionIndex = parseInt(e.key) - 1;
        if (sections[sectionIndex]) {
          scrollToSection(sections[sectionIndex]);
          announceToScreenReader(
            t("accessibility.navigatedTo", "Navegando a") +
              " " +
              t(`home.${sections[sectionIndex]}.title`),
          );
        }
      }
    };

    // Función helper para anunciar a lectores de pantalla
    const announceToScreenReader = (text) => {
      const announcement = document.createElement("div");
      announcement.setAttribute("role", "status");
      announcement.setAttribute("aria-live", "polite");
      announcement.textContent = text;
      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 3000);
    };

    window.addEventListener("keydown", handleKeyNavigation);
    return () => window.removeEventListener("keydown", handleKeyNavigation);
  }, [activeSection, t]);

  useEffect(() => {
    // Crear y actualizar schema.org JSON-LD para SEO
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-webpage";

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: t("home.hero.title", "VicDown - Comunidad para síndrome de Down"),
      description: t("home.hero.description"),
      url: window.location.href,
      publisher: {
        "@type": "Organization",
        name: "VicDown",
        logo: {
          "@type": "ImageObject",
          url: window.location.origin + "/assets/logo.png",
        },
      },
    };

    script.text = JSON.stringify(schemaData);

    // Reemplazar si ya existe
    const existingScript = document.getElementById("schema-webpage");
    if (existingScript) {
      existingScript.replaceWith(script);
    } else {
      document.head.appendChild(script);
    }

    return () => {
      if (document.getElementById("schema-webpage")) {
        document.getElementById("schema-webpage").remove();
      }
    };
  }, [t, activeSection]);

  // Mejorar la gestión de errores en las imágenes:
  const handleImageError = useCallback(
    (e, fallbackPath, altText) => {
      console.error(`Error loading image: ${e.target.src}`);
      e.target.onerror = null; // Evitar bucles
      e.target.src = fallbackPath || "/assets/images/placeholder.png";

      // Anunciar a lectores de pantalla
      const announcement = document.createElement("div");
      announcement.setAttribute("role", "alert");
      announcement.textContent =
        t("accessibility.imageNotLoaded", "Imagen no disponible") +
        (altText ? `: ${altText}` : "");
      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 3000);
    },
    [t],
  );

  return (
    <>
      {" "}
      {/* Reemplazar por Fragment */}
      <Helmet>
        <link rel="canonical" href="https://vicdown.com/" />
      </Helmet>
      {/* HERO SECTION - Más emotiva y conectada */}
      <section id="hero" ref={heroRef}>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            py: { xs: 10, md: 14 },
            textAlign: "center",
            backgroundImage: "url(/assets/images/general/hero-background.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)", // Mayor oscuridad para mejor contraste
              zIndex: 1,
            },
          }}
        >
          {/* Mejora con más partículas emotivas animadas */}
          <Box
            component={motion.div}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              left: "10%",
              top: "15%",
              zIndex: 2,
            }}
            role="presentation"
            aria-hidden="true" // Mejora de accesibilidad
          >
            <FavoriteIcon
              sx={{
                fontSize: { xs: 30, md: 50 },
                color: theme.palette.secondary.light,
                filter: "drop-shadow(0 0 12px rgba(255,255,255,0.8))", // Más brillo
              }}
            />
          </Box>

          {/* Añade más elementos emotivos */}
          <Box
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 0.3,
            }}
            sx={{
              position: "absolute",
              left: "80%",
              top: "65%",
              zIndex: 2,
            }}
            role="presentation"
            aria-hidden="true"
          >
            <GroupIcon
              sx={{
                fontSize: { xs: 35, md: 55 },
                color: theme.palette.secondary.light,
                filter: "drop-shadow(0 0 12px rgba(255,255,255,0.8))",
              }}
            />
          </Box>

          <Box
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 2 }}
            sx={{
              position: "absolute",
              right: "15%",
              top: "25%",
              zIndex: 2,
            }}
            role="presentation"
          >
            <AccessibilityNewIcon
              sx={{
                fontSize: { xs: 40, md: 60 },
                color: theme.palette.secondary.light,
                filter: "drop-shadow(0 0 8px rgba(255,255,255,0.6))",
              }}
            />
          </Box>

          <Container
            maxWidth="md"
            sx={{
              position: "relative",
              zIndex: 2,
              px: isTablet ? 2 : 3, // Padding horizontal ajustado según el dispositivo
            }}
          >
            <motion.div
              initial={
                isLowPowerDevice
                  ? { opacity: 1, scale: 1 }
                  : { scale: 0.8, opacity: 0 }
              }
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: isLowPowerDevice ? 50 : 100,
                damping: isLowPowerDevice ? 15 : 10,
                duration: isLowPowerDevice ? 0.5 : 1,
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                className="hero-title"
                sx={{
                  color: "#FFFFFF",
                  fontFamily: "'Comfortaa', 'Quicksand', sans-serif",
                  fontSize: { xs: "2.5rem", sm: "3.8rem", md: "4.5rem" }, // Reducido tamaño para evitar desbordamiento
                  fontWeight: 600,
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                  mb: 2,
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  whiteSpace: "normal", // Cambiado a normal para permitir saltos de línea si es necesario
                  overflow: "hidden", // Evitar desbordamiento
                  textAlign: "center", // Centrar el texto
                  maxWidth: "100%", // Limitar al ancho máximo del contenedor
                }}
              >
                {t("home.hero.titlePart1", "Comunidad para ")}
                <Box
                  component="span"
                  sx={{
                    display: "inline",
                    background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    position: "relative",
                    textShadow: "none",
                  }}
                >
                  Vic
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "inline",
                    color: "#FFFFFF",
                  }}
                >
                  Down
                </Box>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  color: "#FFFFFF",
                  fontFamily: "'Comfortaa', 'Quicksand', sans-serif",
                  fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
                  fontWeight: 500,
                  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.5)",
                  mb: 3,
                  letterSpacing: "0.03em",
                  maxWidth: "95%", // Aumentado para mejor visibilidad
                  mx: "auto",
                  lineHeight: 1.5,
                  textAlign: "center", // Centrado explícito
                }}
              >
                {t(
                  "home.hero.subtitle",
                  "Un espacio seguro para familias y especialistas en un mundo más inclusivo",
                )}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#FFFFFF",
                  mb: 4,
                  maxWidth: "700px",
                  mx: "auto",
                  fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
                  lineHeight: 1.7,
                  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
                }}
              >
                {t("home.hero.description")}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                color="secondary"
                size="large"
                component={RouterLink}
                to="/register"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 5,
                  py: { xs: 2, md: 1.8 }, // Mayor padding vertical en móvil
                  fontSize: { xs: "1.2rem", sm: "1.3rem" },
                  fontWeight: 600,
                  borderRadius: "30px",
                  minWidth: { xs: "200px", sm: "auto" }, // Asegurar tamaño mínimo en móvil
                  minHeight: { xs: "56px", sm: "auto" }, // Mayor altura en móvil
                  background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: "0 4px 14px rgba(255, 255, 255, 0.3)",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "hidden",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                    transition: "all 0.6s",
                    animation: "shine 3s infinite",
                  },
                  "@keyframes shine": {
                    "0%": { left: "-100%" },
                    "50%": { left: "100%" },
                    "100%": { left: "100%" },
                  },
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(255, 255, 255, 0.4)",
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(2px)",
                  },
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "-100%",
                    width: "100%",
                    height: "2px",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}, transparent)`,
                    animation: "shine 3s infinite",
                  },
                }}
                aria-label={t(
                  "home.hero.buttonAriaLabel",
                  "Regístrate y únete a la comunidad de VicDown",
                )}
              >
                {t("home.hero.button")}
              </Button>
            </motion.div>

            {/* Indicador de scroll */}
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 1 }}
              sx={{
                position: "absolute",
                bottom: -60,
                left: "50%",
                transform: "translateX(-50%)",
                cursor: "pointer",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              onClick={() => scrollToSection("about")}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  mb: 1,
                  textShadow: "0 1px 3px rgba(0,0,0,0.7)",
                }}
              >
                {t("home.scrollToDiscover", "Descubre más")}
              </Typography>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                aria-hidden="true"
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                  }}
                >
                  <ArrowForwardIcon
                    sx={{
                      transform: "rotate(90deg)",
                      color: theme.palette.primary.main,
                      fontSize: 28,
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </Container>
        </Box>
      </section>
      {/* ABOUT SECTION - Con ilustraciones y más emotividad */}
      <section id="about">
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontFamily: "'Quicksand', sans-serif",
                    fontSize: { xs: "2.2rem", sm: "2.7rem", md: "3.2rem" },
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  {t("home.about.title")}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "1.1rem", sm: "1.3rem" },
                    color: theme.palette.text.primary,
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  {t("home.about.description")}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "1.1rem", sm: "1.3rem" },
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    mb: 4,
                    fontStyle: "italic",
                  }}
                >
                  {t(
                    "home.about.mission",
                    "Nuestra misión es crear un espacio donde las familias    encuentren apoyo, información y una comunidad que comprende sus experiencias.",
                  )}
                </Typography>

                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    // Asegúrate que esta línea NO esté comentada y use setStoryModalOpen
                    onClick={() => setStoryModalOpen(true)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: "10px",
                      borderWidth: "2px",
                      "&:hover": {
                        borderWidth: "2px",
                        backgroundColor: "rgba(168, 197, 225, 0.1)",
                      },
                    }}
                    aria-label={t(
                      "home.about.buttonAriaLabel",
                      "Conoce más sobre VicDown",
                    )}
                  >
                    {t("home.about.button", "Conoce nuestra historia")}
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src="/assets/images/general/about-illustration.png"
                  alt={safeTranslate(
                    "home.about.imageAlt",
                    "Ilustración de familia con persona con síndrome de Down",
                    t,
                  )}
                  loading="lazy"
                  srcSet="/assets/images/general/about-illustration-small.png 400w, 
                         /assets/images/general/about-illustration-medium.png 600w,
                         /assets/images/general/about-illustration.png 800w"
                  sizes="(max-width: 600px) 400px, (max-width: 960px) 600px, 800px"
                  onError={(e) =>
                    handleImageError(
                      e,
                      "/assets/images/placeholder.png",
                      t("home.about.imageAlt", "Ilustración de familia"),
                    )
                  }
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxWidth: 500,
                    mx: "auto",
                    display: "block",
                    borderRadius: "20px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  }}
                />
              </motion.div>

              {/* Etiquetas emotivas */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                  mt: 3,
                }}
              >
                {["amor", "inclusión", "comprensión", "apoyo", "comunidad"].map(
                  (tag, index) => (
                    <motion.div
                      key={tag}
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
                      whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                    >
                      <Chip
                        label={t(`home.about.tags.${tag}`, tag)}
                        color={index % 2 ? "primary" : "secondary"}
                        sx={{
                          px: 1,
                          fontSize: "0.95rem",
                          fontWeight: 600,
                        }}
                      />
                    </motion.div>
                  ),
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </section>
      {/* SECCIÓN MEJORADA: Inspiración y Diálogo Comunitario */}
      <section
        id="inspiration-dialogue"
        aria-labelledby="inspiration-dialogue-title"
      >
        <Box
          sx={{
            py: { xs: 10, md: 14 }, // Mayor padding vertical para sensación de espacio
            // Fondo con degradado elegante usando colores del tema
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.dark,
                    0.2,
                  )}, ${alpha(theme.palette.secondary.dark, 0.25)})`
                : `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.light,
                    0.1,
                  )}, ${alpha(theme.palette.secondary.light, 0.15)})`,
            position: "relative",
            overflow: "hidden",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            // Espacio consistente con otras secciones
            mb: { xs: 6, md: 8 },
            // Sutil efecto de sombra para destacar la sección
            boxShadow:
              theme.palette.mode === "dark"
                ? "inset 0 10px 20px -10px rgba(0,0,0,0.3), inset 0 -10px 20px -10px rgba(0,0,0,0.3)"
                : "inset 0 10px 20px -10px rgba(0,0,0,0.1), inset 0 -10px 20px -10px rgba(0,0,0,0.1)",
          }}
        >
          {/* Elementos decorativos sutiles para añadir profundidad */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0.03,
              backgroundImage: "url(/assets/images/general/pattern-bg.png)", // Patrón sutil
              backgroundSize: "cover",
              zIndex: 0,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            {/* Título principal de la sección (opcional - mostrado solo en pantallas grandes) */}
            <Box
              component={motion.div}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              sx={{
                mb: { xs: 5, md: 7 },
                display: { xs: "none", md: "block" },
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                id="inspiration-dialogue-title"
                align="center"
                sx={{
                  fontSize: { md: "2.7rem" },
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                  fontFamily: "'Quicksand', sans-serif",
                }}
              >
                {safeTranslate(
                  "home.inspiration.title",
                  "Nuestra Comunidad",
                  t,
                )}
              </Typography>
            </Box>

            <Grid
              container
              spacing={{ xs: 6, md: 8 }}
              alignItems="center"
              justifyContent="center"
            >
              {/* Columna 1: Cita Inspiradora */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ x: -60, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, type: "spring", stiffness: 50 }}
                >
                  <Box
                    sx={{
                      textAlign: { xs: "center", md: "left" },
                      borderLeft: {
                        md: `4px solid ${theme.palette.secondary.light}`,
                      },
                      pl: { md: 4 },
                      pr: { md: 2 },
                      position: "relative",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="blockquote"
                      sx={{
                        fontFamily: "'Comfortaa', cursive",
                        fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.7rem" },
                        fontStyle: "italic",
                        color: theme.palette.primary.dark, // Ahora en azul como pediste
                        lineHeight: 1.8,
                        mb: 2,
                        position: "relative",
                        paddingLeft: { xs: "30px", md: "40px" },
                        // Aquí está el problema - hay que modificar el pseudo-elemento
                        "&::before": {
                          content: '""',
                          fontSize: "5rem", // Simplificar para evitar el problema
                          fontFamily: "serif",
                          position: "absolute",
                          left: { xs: 0, md: 0 },
                          top: "-20px",
                          color: theme.palette.secondary.light,
                          opacity: 0.6,
                          lineHeight: 1,
                        },
                      }}
                    >
                      {safeTranslate(
                        "home.pulse.quote",
                        "La inclusión no es una estrategia para ayudar a las personas a encajar en los sistemas y estructuras existentes... Es transformar esos sistemas y estructuras para hacerlos mejores para todos.",
                        t,
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.primary.main, // Autor en azul también
                        fontWeight: 500,
                        textAlign: { xs: "center", md: "right" },
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "1.1rem",
                      }}
                    >
                      —{" "}
                      {safeTranslate(
                        "home.pulse.quoteAuthor",
                        "Diane Richler (Adaptado)",
                        t,
                      )}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>

              {/* Columna 2: Último tema del foro con diseño mejorado */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ x: 60, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.15,
                    type: "spring",
                    stiffness: 50,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        theme.palette.mode === "dark" ? 0.12 : 0.7,
                      ),
                      borderRadius: "16px",
                      p: { xs: 3, md: 4 },
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 8px 32px rgba(0,0,0,0.3)"
                          : "0 8px 32px rgba(0,0,0,0.08)",
                      borderRight: {
                        md: `4px solid ${theme.palette.primary.main}`,
                      },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h3"
                      component="h3"
                      sx={{
                        fontFamily: "'Comfortaa', cursive", // Misma fuente que la cita
                        fontSize: { xs: "1.7rem", md: "2rem" },
                        color: theme.palette.primary.dark,
                        fontWeight: 600,
                        mb: 2.5,
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      {safeTranslate(
                        "home.pulse.forumTitle",
                        "Voces de la Comunidad",
                        t,
                      )}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: "1.1rem", md: "1.15rem" },
                        color: theme.palette.primary.main,
                        mb: 3,
                        lineHeight: 1.6,
                        textAlign: { xs: "center", md: "left" },
                        fontFamily: "'Quicksand', sans-serif",
                      }}
                    >
                      {safeTranslate(
                        "home.pulse.forumDescription",
                        "Un espacio donde compartir experiencias enriquece a todos. Cada historia y pregunta nos hace crecer juntos.",
                        t,
                      )}
                    </Typography>

                    {/* Contenedor del tema más reciente */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: { xs: "center", md: "flex-start" },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: "1.1rem",
                          mb: 2,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          "&::before": {
                            content: '""',
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.secondary.main,
                            marginRight: 1.5,
                          },
                        }}
                      >
                        {safeTranslate(
                          "home.pulse.latestTopic",
                          "Última conversación:",
                          t,
                        )}
                      </Typography>
                      {loadingLatestTopic ? (
                        <Skeleton
                          variant="text"
                          width="85%"
                          height={40}
                          sx={{ bgcolor: theme.palette.action.hover }}
                        />
                      ) : latestTopic ? (
                        <Paper
                          elevation={0} // Sin sombra, más integrado
                          variant="outlined" // Borde sutil
                          sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: "16px", // Bordes suaves
                            borderColor: theme.palette.divider,
                            backgroundColor: "transparent", // Fondo transparente
                            width: "100%",
                            maxWidth: "500px", // Limitar ancho
                            transition: "border-color 0.3s ease",
                            "&:hover": {
                              borderColor: theme.palette.primary.light, // Resaltar borde al pasar el ratón
                            },
                          }}
                        >
                          <Typography
                            variant="h6"
                            component={RouterLink} // Enlace al tema
                            to={`/forum/topic/${latestTopic.id}`} // Ajusta la ruta según tu estructura
                            sx={{
                              fontFamily: "'Quicksand', sans-serif", // Fuente suave
                              fontWeight: 500,
                              fontSize: { xs: "1.1rem", md: "1.2rem" },
                              color: theme.palette.primary.main,
                              textDecoration: "none",
                              display: "block", // Asegurar que ocupe el ancho
                              textAlign: { xs: "center", md: "left" },
                              "&:hover": {
                                color: theme.palette.primary.dark, // Oscurecer al pasar el ratón
                              },
                            }}
                          >
                            {latestTopic.title}
                          </Typography>
                          {/* Podrías añadir detalles como "Publicado hace X" si tienes la fecha */}
                        </Paper>
                      ) : (
                        <Typography
                          sx={{
                            color: theme.palette.text.secondary,
                            fontStyle: "italic",
                            textAlign: { xs: "center", md: "left" },
                          }}
                        >
                          {safeTranslate(
                            "home.pulse.noTopics",
                            "El foro espera tu voz. ¡Inicia una conversación!",
                            t,
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </section>
      {/* FEATURES SECTION - Más interactivo y visual */}
      <section id="features">
        <ScreenReaderOnly>
          {t(
            "home.features.screenReaderIntro",
            "Sección de características principales de VicDown",
          )}
        </ScreenReaderOnly>

        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(58, 142, 186, 0.2)"
                : "rgba(168, 197, 225, 0.2)",
            py: { xs: 8, md: 12 },
            position: "relative",
            overflow: "hidden",
            borderRadius: { md: "50px 0 50px 0" },
            my: { xs: 4, md: 6 },
          }}
        >
          {/* Elementos decorativos */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0.1,
              backgroundImage: "url(/assets/images/general/pattern-bg.png)",
              backgroundSize: "cover",
              zIndex: 0,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7 }}
            >
              <Typography
                variant="h2"
                component="h2"
                align="center"
                gutterBottom
                sx={{
                  fontFamily: "'Quicksand', sans-serif",
                  fontSize: { xs: "2.2rem", sm: "2.7rem", md: "3.2rem" },
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                  mb: { xs: 4, md: 6 },
                }}
              >
                {t("home.features.title")}
              </Typography>
            </motion.div>

            <Grid
              container
              spacing={4}
              justifyContent="center"
              component={motion.div}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {["blog", "specialists", "forum", "chatbot"].map(
                (feature, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={feature}
                    component={motion.div}
                    variants={itemVariants}
                    sx={{ display: "flex" }}
                  >
                    <MemoizedFeatureCard
                      feature={feature}
                      hovered={hoveredFeature}
                      onHover={handleFeatureHover}
                      onClick={handleFeatureClick}
                      icons={featureIcons}
                    />
                  </Grid>
                ),
              )}
            </Grid>

            {/* Añadir esta condición en la sección FEATURES para usar el componente importado */}
            {latestPosts.length === 0 && !loading && (
              <Suspense fallback={<CircularProgress />}>
                <FeatureShowcase />
              </Suspense>
            )}
          </Container>
        </Box>
      </section>
      {/* TESTIMONIALS SECTION - Nueva sección emotiva */}
      <section id="testimonials">
        <Suspense
          fallback={
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          }
        >
          <motion.div
            whileHover={{
              scale: 1.02,
              boxShadow: "0 15px 30px rgba(0,0,0,0.2)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Contenido del testimonio */}
            <Testimonials />
          </motion.div>
        </Suspense>
      </section>
      {/* VIC SPECIAL SECTION - Más llamativo e interactivo */}
      <section id="special-vic">
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            borderRadius: { md: "0 50px 0 50px" },
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Elementos decorativos */}
          <Box
            component={motion.div}
            animate={{
              opacity: [0.7, 1, 0.7],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
            }}
            sx={{
              position: "absolute",
              top: "10%",
              right: "15%",
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)",
            }}
          />

          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  // Aplicar animación inicial solo si NO es móvil
                  initial={
                    isMobile ? { x: 0, opacity: 1 } : { x: -50, opacity: 0 }
                  }
                  // whileInView se mantiene igual, pero solo tendrá efecto si initial fue diferente
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  // La transición solo se aplica si hay animación
                  transition={isMobile ? { duration: 0 } : { duration: 0.8 }}
                >
                  <Typography
                    variant="h2"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontFamily: "'Quicksand', sans-serif",
                      fontSize: { xs: "2.2rem", sm: "2.7rem", md: "3.2rem" },
                      fontWeight: 700,
                      mb: 3,
                      textShadow: "1px 1px 3px rgba(0, 0, 0, 0.3)",
                    }}
                  >
                    {t("home.specialVic.title")}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.4rem" },
                      lineHeight: 1.8,
                      mb: 4,
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {t("home.specialVic.description")}
                  </Typography>

                  <Box sx={{ mb: 5 }}>
                    {["expertise", "access", "support"].map(
                      (benefit, index) => (
                        <motion.div
                          key={benefit}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.2, duration: 0.5 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.secondary.main,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              }}
                            >
                              <CheckCircle
                                sx={{ color: theme.palette.primary.dark }}
                              />
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                fontSize: "1.2rem",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              {t(
                                `home.specialVic.benefits.${benefit}.title`,
                                benefit,
                              )}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              ml: 7.5,
                              mb: 3,
                              fontSize: "1rem",
                              opacity: 0.9,
                            }}
                          >
                            {t(
                              `home.specialVic.benefits.${benefit}.description`,
                              `Descripción de ${benefit}`,
                            )}
                          </Typography>
                        </motion.div>
                      ),
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 3,
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "2rem", sm: "2.5rem" },
                        color: theme.palette.secondary.light,
                        textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      {t("subscription.price")}
                    </Typography>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        component={RouterLink}
                        to="/subscriptions" // CORRECTO (plural)
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          px: 5,
                          py: 1.5,
                          fontSize: "1.2rem",
                          fontWeight: 600,
                          borderRadius: "30px",
                          boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
                          "&:hover": {
                            boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                          },
                        }}
                        aria-label={t(
                          "subscription.buttonAriaLabel",
                          "Suscríbete a VicDown",
                        )}
                      >
                        {t("subscription.button")}
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={6} sx={{ position: "relative" }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.8 }}
                >
                  {/* 
                  <Box
                    component="img"
                    src="/assets/images/chatbot/vic-logo.png"
                    alt="Personaje Vic"
                    onError={(e) => {
                      console.log('Intentando cargar imagen alternativa');
                      e.target.onerror = null; // Evita bucles infinitos
                      e.target.src = '/assets/images/placeholder.png'; // Imagen de respaldo
                    }}
                    sx={{
                      maxWidth: '100%',
                      height: 'auto',
                      filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
                      transform: 'scale(1.1)',
                      mx: 'auto',
                      display: 'block',
                      position: 'relative',
                      zIndex: 1,
                    }}
                    loading="lazy"
                  />
                  */}

                  {/* Burbuja de mensaje animada */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1, duration: 0.5, type: "spring" }}
                    sx={{
                      position: "absolute",
                      top: "10%",
                      right: isMobile ? "5%" : "20%",
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      padding: 2.5,
                      borderRadius: "16px",
                      maxWidth: "240px", // Ligeramente más ancho para acomodar la imagen
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      zIndex: 2,
                      "&:after": {
                        content: '""',
                        position: "absolute",
                        bottom: -10,
                        left: 20,
                        width: 0,
                        height: 0,
                        borderLeft: "10px solid transparent",
                        borderRight: "10px solid transparent",
                        borderTop: `10px solid ${theme.palette.background.paper}`,
                      },
                    }}
                  >
                    {/* 3. Agregar la imagen pequeña de Vic dentro de la burbuja */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                    >
                      <Box
                        component="img"
                        src="/assets/images/chatbot/vic-logo.png"
                        alt="Vic"
                        sx={{
                          width: 55, // Ligeramente más grande
                          height: 55,
                          borderRadius: "50%",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.3)", // Sombra más pronunciada
                          filter: "contrast(1.2) brightness(1.1) saturate(1.1)", // Mejores valores para mayor nitidez
                          border: `2px solid ${theme.palette.primary.main}`,
                          display: "block", // Aseguramos que se muestre como bloque
                          objectFit: "cover", // Aseguramos que la imagen cubra bien el espacio
                          zIndex: 5, // Mayor prioridad visual
                        }}
                      />
                    </Box>

                    {/* 4. Mantener el mensaje original */}
                    <Typography variant="body2" fontWeight={500}>
                      <Box component="span">
                        {t("home.specialVic.greeting.prefix", "¡Hola! Soy ")}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          background: `linear-gradient(45deg, #e0c33a 0%, #f7d64a 100%)`, // Cambio para usar el mismo amarillo del hero
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontWeight: 700,
                        }}
                      >
                        Vic
                      </Box>
                      <Box component="span">
                        {t(
                          "home.specialVic.greeting.suffix",
                          ", tu asistente personalizado. ¿En qué puedo ayudarte hoy?",
                        )}
                      </Box>
                    </Typography>

                    {/* 5. Mantener la animación de "escribiendo..." */}
                    <Box
                      component={motion.div}
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        repeatType: "loop",
                      }}
                      sx={{ mt: 1 }}
                    >
                      <Typography variant="caption" color="primary.main">
                        {t("home.specialVic.typing", "escribiendo...")}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </section>
      {/* BLOG SECTION - Más visual y atractivo */}
      <section id="blog">
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant="h2"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: { xs: "2.2rem", sm: "2.7rem", md: "3.2rem" },
                color: theme.palette.primary.main,
                fontWeight: 700,
                mb: { xs: 4, md: 6 },
              }}
            >
              {t("home.blog.title")}
            </Typography>
          </motion.div>

          {loading ? (
            <Grid container spacing={4}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} md={4} key={item}>
                  <Skeleton variant="rectangular" height={300} />
                  <Skeleton />
                  <Skeleton width="60%" />
                </Grid>
              ))}
            </Grid>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : latestPosts.length === 0 ? (
            // Nuevo mensaje visualmente atractivo cuando no hay posts
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  py: 5,
                  px: 3,
                  borderRadius: "20px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(168, 197, 225, 0.1)",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  maxWidth: "800px",
                  mx: "auto",
                }}
              >
                <ArticleIcon
                  sx={{
                    fontSize: 60,
                    color: theme.palette.primary.main,
                    mb: 2,
                    opacity: 0.7,
                  }}
                />
                <Typography
                  variant="h5"
                  color="primary"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {t(
                    "home.blog.noPosts",
                    "Aún no hay publicaciones disponibles",
                  )}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {t(
                    "home.blog.checkBackSoon",
                    "Vuelve pronto para ver nuestros nuevos artículos sobre síndrome de Down",
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  component={RouterLink}
                  to="/forum"
                  endIcon={<ForumIcon />}
                  aria-label={t(
                    "home.blog.visitForumAriaLabel",
                    "Ir al foro de la comunidad VicDown",
                  )}
                  sx={{
                    mt: 2,
                    borderRadius: "10px",
                    borderWidth: "2px",
                    px: 3,
                  }}
                >
                  {t(
                    "home.blog.visitForum",
                    "Mientras tanto, visita nuestro foro",
                  )}
                </Button>
              </Box>
            </motion.div>
          ) : (
            // Mostrar las tarjetas cuando hay posts (sin cambios)
            <Grid container spacing={4}>
              {latestPosts.map((post) => (
                <Grid item xs={12} md={4} key={post.id}>
                  <MemoizedBlogPostCard post={post} />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </section>
      {/* --- FIN DE LA SECCIÓN BLOG --- */}
      {/* --- INICIO: BANNER INFORMATIVO Y DE FEEDBACK (UNIFICADO) --- */}
      {!loadingBanner &&
        bannerConfig?.enabled && ( // Renderiza solo si no está cargando y está habilitado
          <section id="info-banner" aria-labelledby="info-banner-title">
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
              {/* Título Oculto para Accesibilidad */}
              <Typography
                variant="h2"
                component="h2"
                id="info-banner-title"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                {t("home.banner.title", "Información y Sugerencias")}
              </Typography>
              {/* Contenedor Principal del Banner (Paper unificado) */}
              <Paper
                elevation={4} // Sombra sutil
                sx={{
                  borderRadius: "16px", // Bordes redondeados
                  p: { xs: 3, sm: 4 }, // Padding interno
                  // Fondo ligeramente distinto o del tema, con algo de transparencia opcional
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.7)
                      : alpha(theme.palette.background.default, 0.8),
                  backdropFilter: "blur(5px)", // Efecto opcional si quieres transparencia
                  border: `1px solid ${theme.palette.divider}`, // Borde sutil opcional
                  overflow: "hidden", // Asegura que el contenido no se salga
                }}
              >
                <Grid container spacing={4} alignItems="stretch">
                  {" "}
                  {/* Grid para dividir en 2 */}
                  {/* --- Área de Texto (Izquierda o Ancho Completo si no hay imagen) --- */}
                  <Grid item xs={12} md={bannerConfig.imageUrl ? 6 : 12}>
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.7 }}
                      style={{ height: "100%" }} // Para que ocupe toda la altura
                    >
                      {/* Usamos un Box para estructurar el contenido verticalmente */}
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Mostrar el Título si existe */}
                        {bannerConfig.title && (
                          <Typography
                            variant="h5" /* Puedes ajustar h5, h6, etc. */
                            component="h3" /* Semántica adecuada para un título dentro de la sección */
                            gutterBottom
                            sx={{
                              fontWeight: 600, // Hacerlo un poco más prominente
                              color: theme.palette.primary.dark, // Usar color del tema
                            }}
                          >
                            {bannerConfig.title}
                          </Typography>
                        )}

                        {/* Mostrar la Descripción (antes era bannerConfig.text) */}
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 3,
                            flexGrow: 1, // Empuja el botón hacia abajo
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            lineHeight: 1.7,
                            color: theme.palette.text.primary, // Asegurar buen contraste
                          }}
                        >
                          {bannerConfig.text /* Fallback por si acaso */ ||
                            t(
                              "home.banner.defaultText",
                              "Valoramos tu opinión. Ayúdanos a mejorar VicDown.",
                            )}
                        </Typography>

                        {/* Botón de Feedback */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleOpenFeedbackModal} // Llama al handler para abrir el modal
                          sx={{
                            mt: "auto", // Se pega abajo si hay espacio
                            alignSelf: { xs: "stretch", sm: "flex-start" }, // Ancho completo en móvil, normal en otros
                            fontWeight: 600,
                            px: 3,
                            py: 1.2,
                            minHeight: "44px", // Accesibilidad táctil
                          }}
                          aria-label={t(
                            "home.banner.buttonAriaLabel",
                            "Enviar sugerencia o reporte de error",
                          )}
                        >
                          {/* Texto del botón (configurable desde Firestore) */}
                          {bannerConfig.buttonText ||
                            t("home.banner.buttonDefault", "Enviar Sugerencia")}
                        </Button>
                      </Box>
                    </motion.div>
                  </Grid>
                  {/* --- Área de Imagen (Derecha) - Solo si hay imageUrl en Firestore --- */}
                  {bannerConfig.imageUrl && (
                    <Grid item xs={12} md={6}>
                      <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        style={{ height: "100%" }} // Para que ocupe toda la altura
                      >
                        {/* Box para contener la imagen */}
                        <Box
                          sx={{
                            height: "100%",
                            borderRadius: "12px", // Radio interno para la imagen
                            overflow: "hidden", // Recorta la imagen a los bordes
                            position: "relative", // Para posible overlay o spinner de carga
                            backgroundColor: theme.palette.action.hover, // Fondo mientras carga
                            minHeight: { xs: 200, sm: 250, md: "100%" }, // Altura mínima responsiva
                          }}
                        >
                          <Box
                            component="img"
                            src={bannerConfig.imageUrl} // URL desde Firestore
                            alt={
                              bannerConfig.imageAlt ||
                              t(
                                "home.banner.imageAltDefault",
                                "Imagen informativa sobre VicDown",
                              )
                            } // Alt text desde Firestore
                            loading="lazy" // Carga diferida
                            // Asegúrate de tener handleImageError definido o quita esta línea
                            onError={(e) =>
                              handleImageError(
                                e,
                                "/assets/images/placeholder.png",
                                bannerConfig.imageAlt ||
                                  t(
                                    "home.banner.imageAltDefault",
                                    "Imagen informativa",
                                  ),
                              )
                            }
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover", // Cubre el contenedor sin deformarse
                              display: "block", // Evita espacio extra debajo
                            }}
                          />
                        </Box>
                      </motion.div>
                    </Grid>
                  )}
                </Grid>{" "}
                {/* Fin Grid container */}
              </Paper>{" "}
              {/* Fin Paper principal del banner */}
            </Container>
          </section>
        )}
      {/* --- FIN: BANNER INFORMATIVO Y DE FEEDBACK --- */}
      {/* --- MODALES Y BOTONES FLOTANTES (EXISTENTES) --- */}
      {/* ... tu código existente para SubscriptionModal, ChatbotWindow, ChatbotButton, etc. ... */}
      <>
        {/* Resto del código... */}

        {/* Modal para usuarios no suscritos */}
        <SubscriptionModal
          open={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
        />

        {/* El chatbot solo para suscritos */}
        {isSubscribed && (
          <ChatbotWindow
            isOpen={chatbotWindowOpen}
            onClose={() => setChatbotWindowOpen(false)}
            onMinimize={() => setChatbotWindowOpen(false)}
            onNewMessage={() =>
              !chatbotWindowOpen && setHasUnreadMessages(true)
            }
          />
        )}

        {/* Botón visible para todos */}
        <ChatbotButton
          onClick={handleOpenChatWindow}
          position={{
            bottom: isMobile ? 20 : 30,
            right: isMobile ? 20 : 30,
          }}
          size={isMobile ? 55 : 65}
          showBadge={hasUnreadMessages && !chatbotWindowOpen}
        />

        {/* --- MODALES --- */}
        {/* Modal para usuarios no suscritos */}
        <SubscriptionModal
          open={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
        />

        {/* Modal de la historia */}
        <StoryModal
          open={storyModalOpen}
          onClose={() => setStoryModalOpen(false)}
        />

        {/* --- MODAL DE FEEDBACK --- */}
        <FeedbackModal
          open={isFeedbackModalOpen}
          onClose={handleCloseFeedbackModal}
        />

        {/* Comentario corregido: */}
        {/* Aquí podrían ir otros elementos flotantes o modales si los hubiera */}
      </>
      {/* Cierre del Fragment principal */}
    </>
  );
};

export default Home;
